import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

interface PlatformUsagePreviewItem {
  label?: string;
  run_id?: string;
  node_id?: string;
  model: string;
  pricing_model?: string;
  provider?: string;
  raw_input_tokens: number;
  raw_output_tokens: number;
  raw_total_tokens: number;
  converted_input_tokens: number;
  converted_output_tokens: number;
  billed_input_tokens: number;
  billed_output_tokens: number;
  rounded_billed_total_tokens: number;
  wholesale_cost_usd: number;
  estimated_cost_usd: number;
  unknown_model?: boolean;
  error?: string;
  warning?: string;
}

interface PlatformUsagePreviewTotals {
  raw_input_tokens: number;
  raw_output_tokens: number;
  raw_total_tokens: number;
  converted_input_tokens: number;
  converted_output_tokens: number;
  billed_input_tokens: number;
  billed_output_tokens: number;
  rounded_billed_total_tokens: number;
  wholesale_cost_usd: number;
  estimated_cost_usd: number;
  unknown_model_count?: number;
}

interface PlatformUsagePreviewData {
  billing_method: string;
  items: PlatformUsagePreviewItem[];
  totals: PlatformUsagePreviewTotals;
}

interface PlatformUsagePreviewDialogProps {
  open: boolean;
  endpoint: string | null;
  title: string;
  onClose: () => void;
}

const formatTokens = (value: number | undefined) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '-';
  return Math.round(Number(value)).toLocaleString();
};

const formatDecimalTokens = (value: number | undefined) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '-';
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatUsd = (value: number | undefined) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '-';
  return `$${Number(value).toFixed(6)}`;
};

export const PlatformUsagePreviewDialog: React.FC<PlatformUsagePreviewDialogProps> = ({
  open,
  endpoint,
  title,
  onClose,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PlatformUsagePreviewData | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!open || !endpoint || !user) return;
      setLoading(true);
      setError(null);
      setData(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const json = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(json?.detail || json?.message || 'Failed to load platform usage preview');
        }

        if (!cancelled) {
          setData(json?.data || json);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load platform usage preview');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [open, endpoint, user]);

  const hasWarnings = useMemo(
    () => Boolean(data?.items?.some((item) => item.warning || item.error || item.unknown_model)),
    [data?.items]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && !error && data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity={hasWarnings ? 'warning' : 'info'} variant="outlined">
              Platform billing method: <strong>{data.billing_method}</strong>. This is a dry run and does not
              increment any platform subscription usage.
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
                <Typography variant="caption" color="text.secondary">Raw Tokens</Typography>
                <Typography variant="h6">{formatTokens(data.totals.raw_total_tokens)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
                <Typography variant="caption" color="text.secondary">Converted Tokens</Typography>
                <Typography variant="h6">
                  {formatDecimalTokens((data.totals.converted_input_tokens || 0) + (data.totals.converted_output_tokens || 0))}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
                <Typography variant="caption" color="text.secondary">Platform Billed Tokens</Typography>
                <Typography variant="h6">{formatTokens(data.totals.rounded_billed_total_tokens)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
                <Typography variant="caption" color="text.secondary">Estimated Platform Cost</Typography>
                <Typography variant="h6">{formatUsd(data.totals.estimated_cost_usd)}</Typography>
              </Paper>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Step</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Raw In</TableCell>
                    <TableCell align="right">Raw Out</TableCell>
                    <TableCell align="right">Converted In</TableCell>
                    <TableCell align="right">Converted Out</TableCell>
                    <TableCell align="right">Billed In</TableCell>
                    <TableCell align="right">Billed Out</TableCell>
                    <TableCell align="right">Billed Total</TableCell>
                    <TableCell align="right">Wholesale</TableCell>
                    <TableCell align="right">Estimated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((item, index) => (
                    <React.Fragment key={`${item.run_id || item.node_id || item.label || index}-${index}`}>
                      <TableRow>
                        <TableCell>{item.label || item.node_id || item.run_id || `Step ${index + 1}`}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{item.model}</Typography>
                          {item.pricing_model && item.pricing_model !== item.model && (
                            <Typography variant="caption" color="text.secondary">
                              priced as {item.pricing_model}
                            </Typography>
                          )}
                          {item.provider && (
                            <Typography variant="caption" color="text.secondary">{item.provider}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{formatTokens(item.raw_input_tokens)}</TableCell>
                        <TableCell align="right">{formatTokens(item.raw_output_tokens)}</TableCell>
                        <TableCell align="right">{formatDecimalTokens(item.converted_input_tokens)}</TableCell>
                        <TableCell align="right">{formatDecimalTokens(item.converted_output_tokens)}</TableCell>
                        <TableCell align="right">{formatDecimalTokens(item.billed_input_tokens)}</TableCell>
                        <TableCell align="right">{formatDecimalTokens(item.billed_output_tokens)}</TableCell>
                        <TableCell align="right">{formatTokens(item.rounded_billed_total_tokens)}</TableCell>
                        <TableCell align="right">{formatUsd(item.wholesale_cost_usd)}</TableCell>
                        <TableCell align="right">{formatUsd(item.estimated_cost_usd)}</TableCell>
                      </TableRow>
                      {(item.warning || item.error || item.unknown_model) && (
                        <TableRow>
                          <TableCell colSpan={11}>
                            <Alert severity={item.error || item.unknown_model ? 'warning' : 'info'} sx={{ py: 0 }}>
                              {item.error || item.warning || 'Unknown model'}
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
